import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const Invite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<{
    email: string;
    role: string;
    customer_id?: string;
    partner_id?: string;
  } | null>(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setError('缺少邀請 token，請檢查邀請連結是否完整');
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/users/invite/verify?token=${token}`
      );

      const data = await response.json();

      if (data.success) {
        setInviteData(data.data);
      } else {
        setError(data.message || '邀請驗證失敗');
      }
    } catch (error) {
      console.error('驗證邀請 token 失敗:', error);
      setError('無法連接到伺服器，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    try {
      setVerifying(true);
      setError(null);

      const auth = getAuth();
      const provider = new GoogleAuthProvider();

      // 使用 Google 登入
      const result = await signInWithPopup(auth, provider);
      const firebaseUid = result.user.uid;

      // 驗證 email 是否匹配
      if (result.user.email?.toLowerCase() !== inviteData?.email.toLowerCase()) {
        setError(`請使用 ${inviteData?.email} 的 Google 帳號登入`);
        setVerifying(false);
        return;
      }

      // 接受邀請
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/users/invite/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            firebase_uid: firebaseUid,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // 根據角色導向對應的首頁
        const role = inviteData?.role;
        if (role === 'admin' || role === 'operator') {
          navigate('/admin/dashboard');
        } else {
          navigate('/login');
        }
      } else {
        setError(data.message || '接受邀請失敗');
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('接受邀請失敗:', error);
      if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/popup-closed-by-user') {
        setError('登入視窗已關閉，請重新嘗試');
      } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        setError(error.message || '接受邀請失敗，請稍後再試');
      } else {
        setError('接受邀請失敗，請稍後再試');
      }
    } finally {
      setVerifying(false);
    }
  };

  const getRoleText = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: '系統管理員',
      operator: '營運人員',
      customer: '客戶',
      partner: '承攬夥伴',
    };
    return roleMap[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">驗證邀請連結中...</p>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">邀請驗證失敗</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              返回首頁
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Teleworker CRM 系統邀請</h2>
          <p className="text-gray-600 mb-6">
            您已被邀請加入 Teleworker CRM 系統
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">郵件：</span>
                <span className="text-sm text-gray-900 ml-2">{inviteData?.email}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">角色：</span>
                <span className="text-sm text-gray-900 ml-2">{getRoleText(inviteData?.role || '')}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleAcceptInvite}
              disabled={verifying}
              className="w-full px-4 py-3 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {verifying ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>處理中...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span>使用 Google 帳號接受邀請</span>
                </>
              )}
            </button>

            <p className="text-xs text-gray-500">
              請使用 <strong>{inviteData?.email}</strong> 的 Google 帳號登入
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invite;

